import type { Command } from "../document/commands.ts";
import type { JsonValue, MapDocument } from "../document/types.ts";
import { withFieldValue, type FieldAddress } from "./field-kinds.ts";
import { setPropBehaviourCommand, updateEntityCommand } from "./panel-commands.ts";
import type { ReferenceThing } from "./references.ts";
import type { PropRef } from "./templates.ts";

/**
 * One field of one thing, or of every selected prop, set as one command (ADR-070's amendments "typed
 * references" and "the other field kinds"): what a pick, a cleared reference, a removed row and a painted area
 * all write through, so each is one undo step on the thing whose field it is.
 */

/**
 * Whose field: one prop, one marker, or every selected prop at once (P1.6-T33's inspector).
 */
export type PickOwner = ReferenceThing | { readonly kind: "props" };

/**
 * What a field edit reads and runs through.
 */
export interface FieldEditHost {
  readonly map: () => MapDocument;
  readonly run: (command: Command) => boolean;
  readonly setManyField: (name: string, value: JsonValue | null) => boolean;
  readonly propSelection: () => readonly PropRef[];
}

/**
 * A thing's field values.
 *
 * @param map The map.
 * @param thing The prop or marker.
 * @returns Its fields, or undefined when it is gone or is a prop without a behaviour.
 */
export function fieldsOfThing(map: MapDocument, thing: ReferenceThing): { readonly [key: string]: JsonValue } | undefined {
  const layer = map.layers.find((l) => l.id === thing.layerId);
  if (thing.kind === "prop" && layer?.kind === "prop") {
    return layer.props.find((p) => p.id === thing.propId)?.behaviour?.fields;
  }
  if (thing.kind === "marker" && layer?.kind === "entity") {
    return layer.entities.find((e) => e.id === thing.entityId)?.fields;
  }
  return undefined;
}

/**
 * The fields an owner's edit starts from: its own, or for every selected prop the first one's, which they
 * share whenever the field is not mixed.
 *
 * @param host The store's side.
 * @param owner Whose field.
 * @returns The fields, or undefined when there are none.
 */
export function ownerFields(host: Pick<FieldEditHost, "map" | "propSelection">, owner: PickOwner): { readonly [key: string]: JsonValue } | undefined {
  if (owner.kind !== "props") {
    return fieldsOfThing(host.map(), owner);
  }
  const first = host.propSelection()[0];
  return first === undefined ? undefined : fieldsOfThing(host.map(), { kind: "prop", layerId: first.layerId, propId: first.propId });
}

/**
 * Sets the value at a field's address as one command: on one prop (`props.setBehaviour`), one marker
 * (`props.setMarkerFields`), or every selected prop (`many.setField`, with the whole field as the first prop
 * holds it after the change).
 *
 * @param host The store's side.
 * @param owner Whose field.
 * @param address The field, or one row of it.
 * @param value The value, or null to clear the field or remove the row.
 * @returns False when nothing changed or the command was refused.
 * @throws Error when the row lies past the list's end.
 */
export function setThingField(host: FieldEditHost, owner: PickOwner, address: FieldAddress, value: JsonValue | null): boolean {
  const fields = ownerFields(host, owner);
  if (fields === undefined) {
    return false;
  }
  const next = withFieldValue(fields, address, value);
  if (owner.kind === "props") {
    return address.index === undefined ? host.setManyField(address.field, value) : next !== null && host.setManyField(address.field, next[address.field] ?? null);
  }
  if (next === null) {
    return false;
  }
  const map = host.map();
  const layer = map.layers.find((l) => l.id === owner.layerId);
  if (owner.kind === "prop" && layer?.kind === "prop") {
    const behaviour = layer.props.find((p) => p.id === owner.propId)?.behaviour;
    return behaviour !== undefined && host.run(setPropBehaviourCommand(map, owner.layerId, owner.propId, { ...behaviour, fields: next }));
  }
  if (owner.kind === "marker" && layer?.kind === "entity") {
    const entity = layer.entities.find((e) => e.id === owner.entityId);
    return entity !== undefined && host.run(updateEntityCommand(map, owner.layerId, owner.entityId, { x: entity.x, y: entity.y, fields: next }));
  }
  return false;
}
